/**
 * La cave, la recave, et la cave des caves.
 *
 * Les vraies règles de comptoir : on ne se quitte pas sur une partie. On joue **la
 * cave**, puis **la recave**, et les deux perdants s'affrontent dans **la cave des
 * caves**. Celui qui perd celle-là paie la tournée.
 *
 * ## Ce que ce module n'est pas
 *
 * Ce n'est **pas** une variante de règles. Chaque cave est une partie ordinaire, avec
 * le même moteur, les mêmes combinaisons et les mêmes tables d'IA — ce qui compte,
 * parce que ces tables sont calibrées sur un RuleSet précis (`sim/`) et qu'en changer
 * demanderait de tout recalculer. Le tournoi ne fait qu'**enchaîner** des parties et
 * retenir qui a perdu.
 *
 * ## Les sièges changent, les joueurs non
 *
 * La finale n'oppose que deux joueurs : les sièges y sont renumérotés. Un joueur est
 * donc identifié par sa place à **la table** (son index dans `table`), et `participants`
 * fait la correspondance avec les sièges de la partie en cours. Confondre les deux
 * ferait payer sa tournée à quelqu'un qui a gagné.
 */
import { creerPartie } from "./moteur.js";
export class CaveNonTerminee extends Error {
}
/** Ouvre la soirée sur la cave, avec toute la table. */
export function ouvrirTournoi(table, regles, format = "SOIREE") {
    const participants = table.map((_, i) => i);
    return {
        etape: "CAVE",
        format,
        partie: partiePour(table, participants, regles),
        table,
        participants,
        perdants: [],
        paye: null,
    };
}
/**
 * Clôt la cave en cours et prépare la suivante — ou désigne qui paie.
 *
 * Trois issues, et la troisième est celle qu'on oublie : après la recave, si le
 * **même** joueur a perdu les deux caves, il n'y a pas de finale à jouer. Sans ce
 * cas, on créerait une partie à un seul joueur, que `creerPartie` refuse : le tournoi
 * planterait juste avant de désigner qui paie.
 */
export function caveSuivante(tournoi, regles) {
    if (tournoi.etape === "FINI")
        return tournoi;
    if (tournoi.partie.perdant === null) {
        throw new CaveNonTerminee("la cave en cours n'a pas encore de perdant");
    }
    const place = tournoi.participants[tournoi.partie.perdant];
    if (place === undefined) {
        throw new CaveNonTerminee(`siège ${tournoi.partie.perdant} inconnu parmi les participants`);
    }
    const perdants = [...tournoi.perdants, place];
    if (tournoi.format === "SIMPLE") {
        // Une seule cave : le perdant de celle-ci paie, il n'y a pas de suite à jouer.
        return { ...tournoi, etape: "FINI", perdants, paye: place };
    }
    if (tournoi.etape === "CAVE") {
        return prochaine(tournoi, "RECAVE", tournoi.table.map((_, i) => i), perdants, regles);
    }
    if (tournoi.etape === "RECAVE") {
        const [premier, second] = perdants;
        if (premier === second) {
            // Perdu deux fois de suite : la question est réglée, on ne joue pas une
            // finale contre soi-même.
            return { ...tournoi, etape: "FINI", perdants, paye: premier };
        }
        return prochaine(tournoi, "FINALE", [premier, second], perdants, regles);
    }
    // Fin de la cave des caves.
    return { ...tournoi, etape: "FINI", perdants, paye: place };
}
function prochaine(tournoi, etape, participants, perdants, regles) {
    return {
        ...tournoi,
        etape,
        participants,
        perdants,
        partie: partiePour(tournoi.table, participants, regles),
    };
}
/** Une partie neuve pour les places données, IA et niveaux reportés. */
function partiePour(table, participants, regles) {
    const places = participants.map((i) => table[i]);
    const base = creerPartie(places.map((p) => p.nom), regles);
    return {
        ...base,
        joueurs: base.joueurs.map((j) => ({
            ...j,
            estIA: places[j.siege].estIA,
            niveauIA: places[j.siege].niveauIA,
        })),
    };
}
//# sourceMappingURL=tournoi.js.map